import Alpine from 'alpinejs';
import gsap from 'gsap';
import ScrollTrigger from 'gsap/ScrollTrigger';

const itemSelector = '[data-tilted-film-strip-item]';

const getItems = (root) => gsap.utils.toArray(root.querySelectorAll(itemSelector));

const setupMobileParallax = (root) => {
    const items = getItems(root);
    const timelines = items.map((item, index) =>
        gsap.fromTo(
            item,
            { y: index % 2 === 0 ? 32 : 16, opacity: 0.78 },
            {
                y: index % 2 === 0 ? -24 : -14,
                opacity: 1,
                ease: 'none',
                scrollTrigger: {
                    trigger: item,
                    start: 'top bottom',
                    end: 'bottom top',
                    scrub: true,
                    invalidateOnRefresh: true,
                },
            }
        )
    );

    return () => {
        timelines.forEach((timeline) => timeline.kill());
        gsap.set(items, { clearProps: 'transform,opacity' });
    };
};

const setupTiltedFilmStrip = (root) => {
    const pin = root.querySelector('.tilted-film-strip__pin');
    const track = root.querySelector('.tilted-film-strip__track');
    const progressFill = root.querySelector('[data-tilted-film-strip-progress]');
    const items = getItems(root);

    if (!pin || !track || !items.length) {
        return null;
    }

    const getDistance = () => Math.max(0, track.scrollWidth - root.getBoundingClientRect().width);

    const timeline = gsap.timeline({
        scrollTrigger: {
            trigger: root,
            start: 'top top',
            end: () => `+=${getDistance()}`,
            pin,
            scrub: true,
            anticipatePin: 1,
            invalidateOnRefresh: true,
            onUpdate: (self) => {
                if (progressFill) {
                    gsap.set(progressFill, { width: `${self.progress * 100}%` });
                }
            },
        },
    });

    timeline.to(track, { x: () => getDistance() * -1, ease: 'none' });

    const itemTimelines = items.map((item, index) =>
        gsap.fromTo(
            item,
            { rotateY: index % 2 === 0 ? 8 : -8, y: index % 2 === 0 ? 28 : -28 },
            {
                rotateY: 0,
                y: 0,
                ease: 'none',
                scrollTrigger: {
                    trigger: root,
                    start: 'top bottom',
                    end: 'bottom top',
                    scrub: true,
                    invalidateOnRefresh: true,
                },
            }
        )
    );

    return () => {
        timeline.kill();
        itemTimelines.forEach((timeline) => timeline?.kill());
        gsap.set([track, ...items], { clearProps: 'transform,opacity' });
    };
};

Alpine.data('tiltedfilmstrip', () => ({
    media: null,

    init() {
        const root = this.$root;

        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            return;
        }

        gsap.registerPlugin(ScrollTrigger);

        this.media = gsap.matchMedia();
        this.media.add('(max-width: 767px)', () => setupMobileParallax(root));
        this.media.add('(min-width: 768px)', () => setupTiltedFilmStrip(root));

        requestAnimationFrame(() => ScrollTrigger.refresh());
    },

    destroy() {
        this.media?.revert();
    },
}));
